import styled, { css } from 'styled-components';
import { DEVICES } from '_constants';
import { deviceLess } from '_constants/DEVICES';
import { UiColors } from 'assets/styles/old/global';
import { Wrapper as TabsWrapper } from 'ui-kit/Tab/tab.style';

const ButtonTab = styled.button.attrs(() => ({ role: 'button', ariaRequired: true, ariaPressed: 'false' }))`
  height: 44px;
  flex-direction: row;
  flex-shrink: 1;
  text-align: center;
  flex-grow: 1;
  width: 100%;
  font-size: 16px;
  font-family: Roboto;
  background: ${UiColors.fillWhiteColor};
  border: 1px ${UiColors.strokeColor} solid;
  border-bottom: ${({ isClicked }) => (isClicked ? `2px  ${UiColors.orangeColor}` : `1px ${UiColors.strokeColor}`)}
    solid;
  box-sizing: border-box;

  @media ${DEVICES.laptop} {
    font-size: 14px;
  }

  @media ${DEVICES.tablet} {
    display: none;
  }

  & + & {
    border-left: none;
  }

  &:hover {
    background: ${UiColors.footerBackgroundColor};
    border: 1px solid ${UiColors.footerBackgroundColor};
    box-shadow: inset 0px 0px 3px rgba(0, 0, 0, 0.22);
  }
  &:focus {
    outline-color: var(--focus) !important;
  }
`;

export const TabsPanelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 0 20px 0 0;
  height: 46px;

  ${({ hasBorders }) =>
    hasBorders &&
    css`
      border-top: 1px ${UiColors.strokeColor} solid;
      border-right: 1px ${UiColors.strokeColor} solid;
      border-left: 1px ${UiColors.strokeColor} solid;
    `}

  ${ButtonTab} {
    flex-shrink: 0;
  }
  ${TabsWrapper} {
    width: 400px;
  }

  @media ${DEVICES.tablet} {
    ${TabsWrapper} {
      width: 150px;
      flex-shrink: 0;
      margin-right: 10px;
    }
    ${TabsWrapper} .BaseSelect__control {
      width: 150px;
    }
  }

  ${({ isHidden }) =>
    isHidden &&
    css`
      display: none;
    `}
`;

export const AmountText = styled.div`
  margin: 0 0 0 auto;
`;

export const MapWrapper = styled.div`
  /*
  * MAP VIEW BASICS: the map div itself, overriding some overly-broad CSS classes impacting these DIVs
  */

  > div {
    height: 689px;
    max-height: 689px;
  }

  img {
    display: inline; /* work around some global style that sets image to block! */
  }

  div.mapViewMainMap div.leaflet-pane {
    vertical-align: unset; /* work around some global style that sets image alignment, and messes with map tiles */
  }

  div.mapViewMainMap.leaflet-container {
    cursor: crosshair !important;
  }

  div.mapViewMainMap,
  div.mapViewStreetView {
    width: 100%;
    height: 100%;
    border: 1px solid #cccccc;
    z-index: 1;
  }

  div.mapViewStreetView {
    display: none;
  }
  div.mapViewStreetView.streetViewEnabled {
    display: block !important;
  }

  /* when StreetView tool is in use, give them half of the height so they can fit onto the screen together */
  div.mapViewMainMap.streetViewEnabled,
  div.mapViewStreetView.streetViewEnabled {
    height: 50%;
  }

  /* New UI: while the retailer popup is docked over the map's right edge, slide the right-hand
     controls (zoom / basemap / pegman) inboard by its width so they stay reachable. The class and
     the custom property are set on <body> by MapPopup — see modules/Modals/old/mapPopup/styles.js.
     The left stack (the "Map Features" panel) is deliberately untouched. */
  @media ${deviceLess.tableView} {
    body.map-popup-docked & {
      div.mapViewMainMap div.leaflet-top.leaflet-right,
      div.mapViewMainMap div.leaflet-bottom.leaflet-right {
        right: var(--map-popup-docked-width, 0px);
        transition: right 120ms ease;
      }
    }
  }

  /* override Leaflet 1.x suppressing the ARIA outline (Leaflet 1.7 and earlier) */
  div.mapViewMainMap :focus,
  div.mapViewMainMap {
    outline: -webkit-focus-ring-color auto thin !important;
    outline: revert !important;
  }

  /*
  * EasyButton creates a button, and thus inherits other button styles form the site
  * make it look more like proper Leaflet controls
  * and some exceptions for some buttons which need auto width instead of icon-only width
  */

  button.easy-button-button {
    background-color: white;
    border: 0 none transparent;
    padding: 0;
    width: 30px;
    height: 30px;
    border-radius: 4px;

    font-family: Roboto;
    font-size: 14px;
  }

  div.leaflet-button-pngexport button,
  div.leaflet-button-print button {
    width: auto;
    padding-left: 5px;
    padding-right: 5px;
  }
  div.leaflet-button-pngexport button img,
  div.leaflet-button-print button img {
    vertical-align: text-bottom;
  }

  /*
  * Safari workaround for Export PNG
  */
  div.mapViewSafariScreenshotInstructions {
    display: none;
  }
  div.mapViewSafariScreenshotInstructions.showingSafariScreenshotInstructions {
    display: block;

    background-color: black;
    color: white;
    padding: 0.5em 1em;
  }
  div.mapViewSafariScreenshotInstructions button {
    background: none;
    border: none;
    cursor: pointer;

    float: right;
    margin-top: -1px;
  }

  /*
  * MAP POPUPS showing retailer info
  */

  div.leaflet-popup-content {
    .location-title {
      font-weight: bold;
    }

    .streetview-button {
      float: right;
      cursor: pointer;
      width: 20px;
    }
  }

  /*
  * PRINT MEDIA to hide some map controls (not all, scale still makes sense in a static printout) when the page is printed
  */
  @media print {
    div.mapViewMainMap .leaflet-biglayerpanel-control,
    div.mapViewMainMap .leaflet-control-basemapbar,
    div.mapViewMainMap .leaflet-control-zoom,
    div.mapViewMainMap .leaflet-credits-control,
    div.mapViewMainMap .leaflet-bar.leaflet-control,
    div.mapViewMainMap .leaflet-control-attribution {
      display: none !important;
    }
  }
`;

export const Wrapper = styled.div`
  ${({ isManageAssignment }) =>
    isManageAssignment &&
    css`
      position: relative;
      left: -25px;
      width: calc(100% + 50px);

      .main-wrapper,
      .table-footer {
        border: none;
      }
    `}
`;

export const TabsContent = styled.div``;

export const ComponentWrapper = styled.div`
  padding: 0;
  margin: 0;
  display: none;

  &.active {
    display: block;
  }
`;
